import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useReduxAuth';
import { useNotification } from './index';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_COLLECTION_BY_FIRM_QUERY,
  INSERT_COLLECTION_REMINDER_MUTATION,
  DELETE_COLLECTION_REMINDER_MUTATION,
  INSERT_PAYMENT_PROMISE_MUTATION,
  DELETE_PAYMENT_PROMISE_MUTATION,
} from '../graphql/queries/collections';
import { UPDATE_COLLECTION_MUTATION } from '../graphql/mutations/collections';

export const useCollectionByFirm = (firmId) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    try {
      setLoading(true);
      const result = await hasuraClient.query(GET_COLLECTION_BY_FIRM_QUERY, { firmId: parseInt(firmId) });
      setCollection(result.collections?.[0] || null);
    } catch {
      showNotification(t('hooks.collections.errors.fetchList', 'Tahsilat yüklenemedi'), 'error');
    } finally {
      setLoading(false);
    }
  }, [firmId, showNotification, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateNote = useCallback(async (note) => {
    if (!collection) return;
    await hasuraClient.mutate(UPDATE_COLLECTION_MUTATION, { id: collection.id, _set: { note } });
    setCollection(prev => ({ ...prev, note }));
  }, [collection]);

  const addReminder = useCallback(async ({ reminder_date, note }) => {
    if (!collection) return;
    try {
      const result = await hasuraClient.mutate(INSERT_COLLECTION_REMINDER_MUTATION, {
        object: { collection_id: collection.id, reminder_date, note: note || null, created_by: user?.id },
      });
      const newReminder = result.insert_collection_reminders_one;
      setCollection(prev => ({
        ...prev,
        reminders: [...(prev.reminders || []), newReminder].sort(
          (a, b) => new Date(a.reminder_date) - new Date(b.reminder_date)
        ),
      }));
      showNotification(t('tahsilat.reminder.addSuccess', 'Hatırlatıcı eklendi'), 'success');
    } catch {
      showNotification(t('tahsilat.reminder.addError', 'Hatırlatıcı eklenemedi'), 'error');
    }
  }, [collection, user?.id, showNotification, t]);

  const deleteReminder = useCallback(async (reminderId) => {
    try {
      await hasuraClient.mutate(DELETE_COLLECTION_REMINDER_MUTATION, { id: reminderId });
      setCollection(prev => ({
        ...prev,
        reminders: (prev.reminders || []).filter(r => r.id !== reminderId),
      }));
    } catch {
      showNotification(t('tahsilat.reminder.deleteError', 'Hatırlatıcı silinemedi'), 'error');
    }
  }, [showNotification, t]);

  const addPaymentPromise = useCallback(async ({ promise_date, amount, note, promised_by }) => {
    if (!collection) return;
    try {
      const result = await hasuraClient.mutate(INSERT_PAYMENT_PROMISE_MUTATION, {
        object: {
          collection_id: collection.id,
          promise_date,
          amount: amount || null,
          note: note || null,
          promised_by,
          created_by: user?.id,
        },
      });
      const newPromise = result.insert_collection_payment_promises_one;
      setCollection(prev => ({
        ...prev,
        payment_promises: [newPromise, ...(prev.payment_promises || [])],
      }));
      showNotification(t('tahsilat.paymentPromise.addSuccess', 'Ödeme sözü eklendi'), 'success');
    } catch {
      showNotification(t('tahsilat.paymentPromise.addError', 'Ödeme sözü eklenemedi'), 'error');
    }
  }, [collection, user?.id, showNotification, t]);

  const deletePaymentPromise = useCallback(async (promiseId) => {
    try {
      await hasuraClient.mutate(DELETE_PAYMENT_PROMISE_MUTATION, { id: promiseId });
      setCollection(prev => ({
        ...prev,
        payment_promises: (prev.payment_promises || []).filter(p => p.id !== promiseId),
      }));
    } catch {
      showNotification(t('tahsilat.paymentPromise.deleteError', 'Ödeme sözü silinemedi'), 'error');
    }
  }, [showNotification, t]);

  return {
    collection,
    loading,
    refetch: fetchData,
    updateNote,
    addReminder,
    deleteReminder,
    addPaymentPromise,
    deletePaymentPromise,
  };
};
